import { useState, useEffect } from 'react';
import { PromptForm } from './components/PromptForm';
import { TaskStatus } from './components/TaskStatus';
import { AuthForm } from './components/AuthForm';
import { UserMenu } from './components/UserMenu';
import { AdminTasks } from './components/AdminTasks';
import { useTaskPolling } from './hooks/useTaskPolling';
import { useAuth } from './hooks/useAuth';
import { apiService } from './services/api';
import ScreenSizeWarning from './components/ScreenSizeWarning';

type Page = 'home' | 'admin';

function getPageFromHash(): Page {
  return window.location.hash === '#/admin' ? 'admin' : 'home';
}

function App() {
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState<Page>(getPageFromHash);
  const { user, isLoading: authLoading, refreshUser } = useAuth();

  const { status, isLoading, error } = useTaskPolling(taskId || '', !!taskId);

  const navigate = (page: Page) => {
    setCurrentPage(page);
    window.location.hash = page === 'admin' ? '#/admin' : '';
  };

  useEffect(() => {
    const onHashChange = () => setCurrentPage(getPageFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleSubmit = async (prompt: string, templateId?: string, includeCoverAndEnd?: boolean, pageCount?: number) => {
    if (!user) {
      alert('请先登录');
      return;
    }

    if (user.remaining_quota <= 0) {
      alert('使用次数已用完，请联系管理员');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiService.createTask(prompt, undefined, templateId, 'fidelity', includeCoverAndEnd, pageCount);
      setTaskId(response.taskId);
      await refreshUser();
    } catch (err) {
      console.error('Failed to create task:', err);
      alert('创建任务失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setTaskId(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-slate-600 font-medium">加载中...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* 优雅的渐变背景 */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50"></div>

        {/* 装饰性背景元素 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* 大圆圈装饰 - 柔和的蓝色系 */}
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-300/15 to-indigo-300/15 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-slate-300/15 to-blue-300/15 rounded-full blur-3xl"></div>

          {/* 中等圆圈装饰 - 淡雅的层次 */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-indigo-200/8 to-blue-200/8 rounded-full blur-2xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-gradient-to-tr from-slate-200/8 to-indigo-200/8 rounded-full blur-2xl"></div>

          {/* 小圆点装饰 - 精致的细节 */}
          <div className="absolute top-20 right-1/4 w-3 h-3 bg-blue-300/20 rounded-full"></div>
          <div className="absolute top-32 right-1/3 w-2 h-2 bg-indigo-300/20 rounded-full"></div>
          <div className="absolute bottom-40 left-1/4 w-2.5 h-2.5 bg-slate-300/20 rounded-full"></div>
          <div className="absolute top-1/2 right-20 w-2 h-2 bg-blue-200/20 rounded-full"></div>
        </div>

        {/* 内容 */}
        <div className="relative min-h-screen flex flex-col items-center justify-center py-12 px-4">
          <div className="w-full max-w-md">
            {/* 头部 */}
            <header className="text-center mb-12 animate-fade-in">
              {/* Logo 图标 */}
              {/*<div className="inline-block mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-xl"></div>
                <div className="relative bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl p-6 shadow-2xl">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
              </div>*/}

              {/* 标题 */}
              <h1 className="text-4xl md:text-4xl font-bold tracking-tight mb-4">
                <span className="bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent">
                  极演 PPT 
                </span>
              </h1>

              {/* 副标题 */}
              {/*<p className="text-slate-600 text-lg font-medium">
                用 AI 创造令人印象深刻的演示文稿
              </p>*/}

              {/* 装饰线 */}
              <div className="mt-6 flex items-center justify-center gap-3">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-slate-200"></div>
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-400"></div>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-slate-200"></div>
              </div>
            </header>

            {/* 登录表单 */}
            <div className="animate-fade-in-up">
              <AuthForm />
            </div>

            {/* 底部信息 */}
            <footer className="mt-12 text-center text-slate-500 text-sm animate-fade-in">
              <p>
                Powered by{' '}
                <span className="font-medium text-slate-700">GLM</span>
                {/*{' + '}
                <span className="font-medium text-slate-700">Playwright</span>
                {' + '}
                <span className="font-medium text-slate-700">pptxgenjs</span>*/}
              </p>
            </footer>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <ScreenSizeWarning />
      {/* 动态背景装饰 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-purple-400/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      </div>

      <div className="relative min-w-[1280px] px-4 py-8">
        <div className="mx-auto max-w-[1400px]">
          <header className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent">
                  {currentPage === 'admin' ? '极演 PPT 管理后台' : '极演 PPT 生成器'}
                </span>
              </h1>
            </div>
            <UserMenu onNavigate={navigate} currentPage={currentPage} />
          </header>

          <main className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-200/50 p-6 md:p-8 border border-white/50">
          {currentPage === 'admin' ? (
            <AdminTasks onBack={() => navigate('home')} />
          ) : !taskId ? (
            <>
              <PromptForm
                onSubmit={handleSubmit}
                isLoading={isSubmitting}
                disabled={user.remaining_quota <= 0}
              />
              {user.remaining_quota <= 0 && (
                <div className="mt-6 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl font-medium">
                  使用次数已用完，请联系管理员充值
                </div>
              )}
            </>
          ) : (
            <div>
              <TaskStatus status={status} isLoading={isLoading} error={error} taskId={taskId} />
              {(status?.status === 'completed' || status?.status === 'failed') && (
                <button
                  onClick={handleReset}
                  className="mt-6 text-blue-600 hover:text-blue-700 font-medium underline underline-offset-4 hover:underline-offset-8 transition-all"
                >
                  生成新的 PPT →
                </button>
              )}
            </div>
          )}
        </main>

        <footer className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            Powered by <span className="font-medium">GLM</span>
          </p>
        </footer>
      </div>
      </div>
    </div>
  );
}

export default App;
